/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B020030 売上明細照会
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000120EigyoNmr,
  B000151NonyuNmr,
  B000200ByJanCdShohinNm2,
  B000200ShohinNm1,
  B020020Get,
  B020030Find,
  B020030FindRequest,
  catchRestError,
} from "clients/rest";
import { B000150TokuiNmr } from "clients/rest/b000/B000150";
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import {
  B020030ShokaKbn,
  B020030Shutsuryokujun,
  getSCode,
  SCode,
  UriageUchiwakeKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import {
  B000130,
  B000140,
  B000150,
  B000200,
  B000202,
  B020010InputData,
} from "pages";
import { B000120 } from "pages/b000/B000120";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { mapIds } from "services";
import { editKbn } from "services/codes";
import { date, startOfMonth } from "services/DateService";
import { authState, focusState, navigateTypeState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
  page: number;
  pageSize: number;
  sort: string[];
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  uriYmdFrom: string;
  uriYmdTo: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  uridenpyoNoFrom: string;
  uridenpyoNoTo: string;
  juchudenpyoNoFrom: string;
  juchudenpyoNoTo: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNmr: string;
  shokaKbn: SCode;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  shutsuryokujun: SCode;
  eigyoCd: string;
  eigyoNmr: string;
  shainCdFrom: string;
  shainCdTo: string;
  chumonNo: string;
  nonyuCd: string;
  nonyushitenCd: string;
  nonyuNmr: string;
  shohinCd: string;
  shohinNm1: string;
  janCd: string;
  shohinNm2: string;
}>;

// テーブル定義
type Data = Partial<{
  gyo: number;
  uchiwakeKbn: number;
  shohinCd: string;
  shohinNm: string;
  juchudenpyoNo: number;
  uridenpyoNo: number;
  chumonNo: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNmr: string;
  nonyuNmr: string;
  urisu: number;
  shukasu: number;
  shainNm: string;
  bumonNm: string;
  uriKin: number;
  arariKin: number;
  arariRitsu: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  uriYmdFrom: "",
  uriYmdTo: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  uridenpyoNoFrom: "",
  uridenpyoNoTo: "",
  juchudenpyoNoFrom: "",
  juchudenpyoNoTo: "",
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNmr: "",
  shokaKbn: getSCode(B020030ShokaKbn, "2"),
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  shutsuryokujun: getSCode(B020030Shutsuryokujun, "0"),
  eigyoCd: "",
  eigyoNmr: "",
  shainCdFrom: "",
  shainCdTo: "",
  chumonNo: "",
  nonyuCd: "",
  nonyushitenCd: "",
  nonyuNmr: "",
  shohinCd: "",
  shohinNm1: "",
  janCd: "",
  shohinNm2: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "4rem", align: "right" },
  {
    field: "uchiwakeKbn",
    title: "区分",
    width: "4rem",
    render: (data) => {
      return getSCode(UriageUchiwakeKbn, data.uchiwakeKbn?.toString() ?? "0")
        ?.label;
    },
  },
  { field: "uriYmd", hidden: true },
  { field: "shohinCd", title: "商品コード", width: "10rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  { field: "juchudenpyoNo", title: "受注No", width: "10rem" },
  { field: "uridenpyoNo", title: "売上No", width: "10rem" },
  { field: "chumonNo", title: "客先No", width: "10rem" },
  { field: "tokuiCd", hidden: true },
  { field: "tokuishitenCd", hidden: true },
  { field: "tokuiNm", title: "得意先", width: "20rem" },
  { field: "nonyuNm", title: "納入先", width: "20rem" },
  {
    field: "urisu",
    title: "売上数　",
    width: "6rem",
    align: "right",
    render: (data) => data.urisu?.toLocaleString(),
  },
  {
    field: "shukasu",
    title: "出荷数　",
    width: "6rem",
    align: "right",
    render: (data) => data.shukasu?.toLocaleString(),
  },
  { field: "shainNm", title: "売上担当者", width: "20rem" },
  { field: "bumonNm", title: "売上担当部門", width: "20rem" },
  {
    field: "uriKin",
    title: "売上金額　",
    width: "8rem",
    align: "right",
    render: (data) => data.uriKin?.toLocaleString(),
  },
  {
    field: "arariKin",
    title: "粗利金額　",
    width: "8rem",
    align: "right",
    render: (data) => data.arariKin?.toLocaleString(),
  },
  { field: "arariRitsu", title: "粗利率　", width: "6rem", align: "right" },
];

const focusId = "B020030";

// B020030 売上明細照会
export const B020030 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 検索条件を格納するステート
const B020030State = atom<SearchedFields>({
  key: "B020030State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 詳細ボタン活性制御
  const [disabledDetailButton, setDisabledDetailButton] = useState(true);
  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // ナビゲーション
  const navigate = useNavigate();
  const setMenuNavigate = useSetRecoilState(navigateTypeState);
  // 検索条件フィールドの定義
  const [searchedFields, setSearchedFields] = useRecoilState(B020030State);

  //商品中分類が入力されているかどうか
  const existShochubun: boolean = methods.watch("shochubun") !== "";
  //商品小分類が入力されているかどうか
  const existShoshobun: boolean = methods.watch("shoshobun") !== "";

  // 初期表示イベント
  useEffect(() => {
    // 検索条件が保存されている
    if (searchedFields.searched) {
      const fields = searchedFields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("uriYmdFrom", fields.uriYmdFrom);
        methods.setValue("uriYmdTo", fields.uriYmdTo);
        methods.setValue("bumonCdFrom", fields.bumonCdFrom);
        methods.setValue("bumonCdTo", fields.bumonCdTo);
        methods.setValue("uridenpyoNoFrom", fields.uridenpyoNoFrom);
        methods.setValue("uridenpyoNoTo", fields.uridenpyoNoTo);
        methods.setValue("juchudenpyoNoFrom", fields.juchudenpyoNoFrom);
        methods.setValue("juchudenpyoNoTo", fields.juchudenpyoNoTo);
        methods.setValue("tokuiCd", fields.tokuiCd);
        methods.setValue("tokuishitenCd", fields.tokuishitenCd);
        methods.setValue("tokuiNmr", fields.tokuiNmr);
        methods.setValue("shokaKbn", fields.shokaKbn);
        methods.setValue("shodaibun", fields.shodaibun);
        methods.setValue("shochubun", fields.shochubun);
        methods.setValue("shoshobun", fields.shoshobun);
        methods.setValue("shutsuryokujun", fields.shutsuryokujun);
        methods.setValue("eigyoCd", fields.eigyoCd);
        methods.setValue("eigyoNmr", fields.eigyoNmr);
        methods.setValue("shainCdFrom", fields.shainCdFrom);
        methods.setValue("shainCdTo", fields.shainCdTo);
        methods.setValue("chumonNo", fields.chumonNo);
        methods.setValue("nonyuCd", fields.nonyuCd);
        methods.setValue("nonyushitenCd", fields.nonyushitenCd);
        methods.setValue("nonyuNmr", fields.nonyuNmr);
        methods.setValue("shohinCd", fields.shohinCd);
        methods.setValue("shohinNm1", fields.shohinNm1);
        methods.setValue("janCd", fields.janCd);
        methods.setValue("shohinNm2", fields.shohinNm2);
        setSearched(true);
        return;
      }
    } else {
      methods.setValue("uriYmdFrom", startOfMonth(date()));
      methods.setValue("uriYmdTo", date());
    }

    // 初期表示
    (async () => {
      await methods.setValue("eigyoCd", auth?.eigyoCd!);

      await B000120EigyoNmr(methods, navigate, setMsg);
      // 初回フォーカス先
      focus(1);
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 各ボタン非活性化
    setDisabledDetailButton(true);
    // 選択されているデータを空にする
    setRowData({});
    (tableRef.current as any).handleClearSelect();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順に応じたソート処理
      let order;
      if (fields.shutsuryokujun?.value === "0") {
        order = ["uriYmd,asc", "uridenpyoNo,asc", "urigyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "1") {
        order = ["shohinCd,asc", "uridenpyoNo,asc", "urigyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "2") {
        order = [
          "tokuiCd,asc",
          "tokuishitenCd,asc",
          "uridenpyoNo,asc",
          "urigyoNo,asc",
        ];
      } else if (fields.shutsuryokujun?.value === "3") {
        order = ["uridenpyoNo,asc", "urigyoNo,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B020030FindRequest = {
        shokaKbn: fields.shokaKbn!.value,
        eigyoCd: fields.eigyoCd!,
        uriYmdFrom: fields.uriYmdFrom,
        uriYmdTo: fields.uriYmdTo,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        uridenpyoNoFrom: fields.uridenpyoNoFrom,
        uridenpyoNoTo: fields.uridenpyoNoTo,
        juchudenpyoNoFrom: fields.juchudenpyoNoFrom,
        juchudenpyoNoTo: fields.juchudenpyoNoTo,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        chumonNo: fields.chumonNo,
        nonyuCd: fields.nonyuCd,
        nonyushitenCd: fields.nonyushitenCd,
        shohinCd: fields.shohinCd,
        janCd: fields.janCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B020030Find(param)
        // 正常値取得
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリック
  const handleDoubleClickTableRow = (data: Data) => {
    // 売上データの検索を行う
    B020020Get({ uridenpyoNo: data.uridenpyoNo?.toString() })
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.reference,
          uriage: value.data,
        };
        // 参照モードフラグがtrueの場合は参照
        if (inputData.uriage.isSansho === false) {
          inputData.editKbn = editKbn.update;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 詳細ボタンクリックイベント
  const handleShowDatail = () => {
    if (typeof rowData === "undefined") {
      return;
    }
    // 売上データの検索を行う
    B020020Get({ uridenpyoNo: rowData.uridenpyoNo?.toString() })
      .then((value) => {
        const inputData: B020010InputData = {
          editKbn: editKbn.reference,
          uriage: value.data,
        };
        // 参照モードフラグがtrueの場合は参照
        if (inputData.uriage.isSansho === false) {
          inputData.editKbn = editKbn.update;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");
        navigate("/b020010", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行クリックイベント
  const handleSelectRow = (_: any, rowData: Data | undefined) => {
    setRowData(undefined);
    setRowData(rowData);
    setDisabledDetailButton(false);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="売上明細照会"
        gridTemplateColumns="6rem 3rem 2rem 1rem 1rem 1rem 2rem 1rem 4rem 15rem 4rem 7rem 5rem 2rem 1rem 2rem 5rem 15rem"
      >
        <SLabel>売上日</SLabel>
        <SDateField
          label="売上日From"
          name="uriYmdFrom"
          gridColumn="span 4"
          {...register(1)}
        />
        <SText>～</SText>
        <SDateField
          lable="売上日To"
          name="uriYmdTo"
          gridColumn="span 3"
          {...register(2)}
        />

        <Box gridColumn="span 2" />

        <>
          <SLabel required>営業所</SLabel>
          <SNumberField
            label="営業所コード"
            name="eigyoCd"
            code
            required
            gridColumn="span 2"
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCd", data.eigyoCd);
              methods.setValue("eigyoNmr", data.eigyoNmr);
            }}
            onBlur={() => B000120EigyoNmr(methods, navigate, setMsg)}
            {...register(16)}
          />
        </>

        <STextField
          label="営業所名"
          name="eigyoNmr"
          disabled
          gridColumn="span 4"
        />

        <SLabel>売上担当部門</SLabel>
        <SNumberField
          lanel="売上担当部門From"
          name="bumonCdFrom"
          code
          size={6}
          gridColumn="span 4"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
          }}
          {...register(3)}
        />
        <SText>～</SText>
        <SNumberField
          lable="売上担当部門To"
          name="bumonCdTo"
          code
          size={6}
          gridColumn="span 3"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
          }}
          {...register(4)}
        />

        <Box gridColumn="span 2" />

        <SLabel>売上担当者</SLabel>
        <SNumberField
          label="売上担当者From"
          name="shainCdFrom"
          code
          size={7}
          gridColumn="span 2"
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
          }}
          {...register(17)}
        />
        <SText>～</SText>
        <SNumberField
          label="売上担当者To"
          name="shainCdTo"
          code
          size={7}
          gridColumn="span 2"
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
          }}
          {...register(18)}
        />

        <Box />

        <SLabel>売上No</SLabel>
        <SNumberField
          label="売上NoFrom"
          name="uridenpyoNoFrom"
          code
          size={10}
          {...register(5)}
          gridColumn="span 4"
        />
        <SText>～</SText>
        <SNumberField
          label="売上NoTo"
          name="uridenpyoNoTo"
          code
          size={10}
          gridColumn="span 3"
          {...register(6)}
        />

        <Box gridColumn="span 9" />

        <SLabel>受注No</SLabel>
        <SNumberField
          label="受注NoFrom"
          name="juchudenpyoNoFrom"
          code
          size={10}
          {...register(7)}
          gridColumn="span 4"
        />
        <SText>～</SText>
        <SNumberField
          label="受注NoTo"
          name="juchudenpyoNoTo"
          code
          size={10}
          gridColumn="span 3"
          {...register(8)}
        />

        <Box gridColumn="span 2" />

        <SLabel>客先No</SLabel>
        <SNumberField
          lable="客先No"
          name="chumonNo"
          code
          size={11}
          gridColumn="span 2"
          {...register(19)}
        />

        <Box gridColumn="span 4" />

        <SLabel>得意先</SLabel>
        <SNumberField
          label="得意先コード"
          name="tokuiCd"
          code
          size={6}
          gridColumn="span 2"
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
          {...register(9)}
        />
        <SNumberField
          label="得意先支店コード"
          name="tokuishitenCd"
          code
          size={4}
          gridColumn="span 4"
          onBlur={() => B000150TokuiNmr(methods, navigate, setMsg)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNmr", data.tokuiNmr);
          }}
          {...register(10)}
        />
        <STextField
          label="得意先名称"
          name="tokuiNmr"
          size={40}
          gridColumn="span 3"
          disabled
        />

        <Box />

        <SLabel>納入先</SLabel>
        <SNumberField
          label="納入先コード"
          name="nonyuCd"
          code
          size={6}
          gridColumn="span 1"
          onBlur={() => B000151NonyuNmr(methods, navigate, setMsg)}
          {...register(20)}
        />
        <SNumberField
          label="納入先支店コード"
          name="nonyushitenCd"
          code
          size={4}
          gridColumn="span 3"
          onBlur={() => B000151NonyuNmr(methods, navigate, setMsg)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("nonyuCd", data.tokuiCd);
            methods.setValue("nonyushitenCd", data.tokuishitenCd);
            methods.setValue("nonyuNmr", data.tokuiNmr);
          }}
          {...register(21)}
        />
        <STextField
          label="納入先名称"
          name="nonyuNmr"
          size={40}
          disabled
          gridColumn="span 2"
        />

        <SLabel>消化区分</SLabel>
        <SCodeSelect
          label="消化区分"
          name="shokaKbn"
          options={B020030ShokaKbn}
          gridColumn="span 4"
          {...register(11)}
        />

        <Box gridColumn="span 13" />

        <SLabel>商品分類</SLabel>
        <SNumberField
          label="商品分類(大)"
          name="shodaibun"
          code
          size={3}
          required={existShochubun}
          {...register(12)}
        />
        <SNumberField
          label="商品分類(中)"
          name="shochubun"
          code
          size={3}
          gridColumn="span 2"
          required={existShoshobun}
          {...register(13)}
        />
        <SNumberField
          label="商品分類(小)"
          name="shoshobun"
          code
          size={3}
          gridColumn="span 3"
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            if (data.shochubun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", "");
              methods.setValue("shoshobun", "");
            } else if (data.shoshobun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", "");
            } else {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", data.shoshobun);
            }
          }}
          {...register(14)}
        />

        <Box gridColumn="span 4" />

        <SLabel>商品</SLabel>
        <SNumberField
          label="商品コード"
          name="shohinCd"
          code
          size={10}
          gridColumn="span 3"
          onBlur={() => B000200ShohinNm1(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNm1", data.shohinNm);
          }}
          {...register(22)}
        />
        <STextField
          label="商品名称1(商品)"
          name="shohinNm1"
          size={40}
          disabled
          gridColumn="span 3"
        />

        <SLabel>出力順</SLabel>
        <SCodeSelect
          label="出力順"
          name="shutsuryokujun"
          options={B020030Shutsuryokujun}
          gridColumn="span 4"
          {...register(15)}
        />

        <Box gridColumn="span 6" />

        <SLabel>JANコード</SLabel>
        <SNumberField
          label="JANコード"
          name="janCd"
          code
          size={13}
          gridColumn="span 3"
          onBlur={() => B000200ByJanCdShohinNm2(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.shohinCd);
            methods.setValue("shohinNm2", data.shohinNm);
          }}
          {...register(23)}
        />
        <STextField
          label="商品名称2(JAN)"
          name="shohinNm2"
          size={40}
          disabled
          gridColumn="span 3"
        />

        <Box />

        <SNormalButton
          label={labels.buttons.search}
          gridRow="10"
          gridColumn="18"
          align="right"
          onClick={handleClickSearchButton}
          {...register(24)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          width={1250}
          height={300}
          onRowClick={handleSelectRow}
          handleDoubleClick={handleDoubleClickTableRow}
          onPageChange={handleResetSearch}
        />
      </SFrame>

      <SFrame>
        <SNormalButton
          label={labels.buttons.detail}
          disabled={disabledDetailButton}
          onClick={handleShowDatail}
          align="right"
          {...register(25)}
        />
      </SFrame>
    </FormProvider>
  );
};
